import React, { useState, useEffect } from 'react';
import ingredientApi from '../../services/ingredientApi';
import './IngredientPicker.css';

/**
 * Derives sensible step, min, and placeholder values from a unit + category string.
 *
 * The unit field from the DB is often just a size abbreviation like "S", "M", "L"
 * with no keyword to match on, so we also check the category field.
 */
const getInputConfig = (unit = '', category = '') => {
  // Strip ALL whitespace (including non-breaking spaces, tabs, etc.)
  const u = unit.replace(/\s+/g, '').toLowerCase();
  const c = category.replace(/\s+/g, '').toLowerCase();
  const combined = `${c} ${u}`;

  // Pure size abbreviations that mean countable items (e.g. unit = "S", "M", "L")
  const isSizeAbbrev = /^(xs|s|m|l|xl|xxl)$/.test(u);

  // Explicit count keywords anywhere in unit or category
  const isCountUnit =
      isSizeAbbrev ||
      /(can|bottle|keg|bag|box|drum|unit|piece|pack|sachet|pouch|carton|jar|container|other)/.test(combined);

  if (isCountUnit) {
    // Use step=5 for size-hinted cans/bottles, step=1 for generic count units
    const step = isSizeAbbrev || /\b(xs|s|m|l|xl|xxl)\b/.test(combined) ? 1 : 1;
    return { step, min: step, placeholder: '0', decimals: 0 };
  }

  if (/\bmg\b/.test(u))
    return { step: 0.1,  min: 0.01, placeholder: '0.0',  decimals: 1 };
  if (/\bg\b|\bgrams?\b/.test(u))
    return { step: 0.1,  min: 0.01, placeholder: '0.0',  decimals: 1 };
  if (/\bkg\b|\bkilograms?\b|\blb\b|\blbs\b|\boz\b|\bounces?\b/.test(u))
    return { step: 0.01, min: 0.01, placeholder: '0.00', decimals: 2 };
  if (/\bml\b|\bmillilitres?\b|\blitr(e|es|s)?\b|\bfl\.?\s*oz\b|\bgal(lon)?\b/.test(u))
    return { step: 0.1,  min: 0.01, placeholder: '0.0',  decimals: 1 };
  if (/(%|percent|ppm|ratio)/.test(u))
    return { step: 0.1,  min: 0.01, placeholder: '0.0',  decimals: 1 };

  // Fallback: if unit is very short (1-2 chars) and unrecognised, treat as a count
  if (u.length <= 2) return { step: 1, min: 1, placeholder: '0', decimals: 0 };

  return { step: 0.01, min: 0.01, placeholder: '0.00', decimals: 2 };
};

const IngredientPicker = ({ selectedIngredients, onIngredientsChange }) => {
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingIngredients, setIsLoadingIngredients] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [quantityErrors, setQuantityErrors] = useState({});

  useEffect(() => { fetchIngredients(); }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.ingredient-search-container')) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchIngredients = async () => {
    setIsLoadingIngredients(true);
    try {
      const response = await ingredientApi.list({ limit: 500 });
      setAvailableIngredients(response.items || []);
    } catch (error) {
      console.error('Error fetching ingredients:', error);
    } finally {
      setIsLoadingIngredients(false);
    }
  };

  const filteredIngredients = availableIngredients.filter((ingredient) => {
    if (ingredient.is_packaging_material) return false;
    const matchesSearch = ingredient.name.toLowerCase().includes(searchTerm.toLowerCase());
    const notSelected = !selectedIngredients.some((s) => s.ingredient_id === ingredient.id);
    return matchesSearch && notSelected;
  });

  const handleAddIngredient = (ingredient) => {
    onIngredientsChange([
      { ingredient_id: ingredient.id, ingredient_name: ingredient.name, category: ingredient.category, unit: ingredient.unit, quantity_per_keg: '' },
      ...selectedIngredients,
    ]);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleRemoveIngredient = (ingredientId) => {
    onIngredientsChange(selectedIngredients.filter((ing) => ing.ingredient_id !== ingredientId));
    const errors = { ...quantityErrors };
    delete errors[ingredientId];
    setQuantityErrors(errors);
  };

  const applyQuantityUpdate = (ingredientId, newValue, decimals) => {
    const rounded = parseFloat(newValue.toFixed(decimals));
    const updated = selectedIngredients.map((ing) =>
        ing.ingredient_id === ingredientId ? { ...ing, quantity_per_keg: rounded } : ing
    );
    onIngredientsChange(updated);

    if (rounded <= 0) {
      setQuantityErrors({ ...quantityErrors, [ingredientId]: 'Quantity must be greater than 0' });
    } else {
      const errors = { ...quantityErrors };
      delete errors[ingredientId];
      setQuantityErrors(errors);
    }
  };

  const handleTypedChange = (ingredientId, value, decimals) => {
    // Allow free typing — only validate, don't round while user is mid-type
    const updated = selectedIngredients.map((ing) =>
        ing.ingredient_id === ingredientId ? { ...ing, quantity_per_keg: value } : ing
    );
    onIngredientsChange(updated);

    const num = parseFloat(value);
    if (value !== '' && (isNaN(num) || num <= 0)) {
      setQuantityErrors({ ...quantityErrors, [ingredientId]: 'Quantity must be greater than 0' });
    } else {
      const errors = { ...quantityErrors };
      delete errors[ingredientId];
      setQuantityErrors(errors);
    }
  };

  const handleStepUp = (ingredientId, currentValue, step, min, decimals) => {
    const current = parseFloat(currentValue) || 0;
    applyQuantityUpdate(ingredientId, Math.max(min, current + step), decimals);
  };

  const handleStepDown = (ingredientId, currentValue, step, min, decimals) => {
    const current = parseFloat(currentValue) || 0;
    const next = current - step;
    if (next < min) return; // don't go below minimum
    applyQuantityUpdate(ingredientId, next, decimals);
  };

  return (
      <div className="ingredient-picker">
        <div className="form-group">
          <label htmlFor="ingredient-search">Add Ingredients</label>
          <div className="ingredient-search-container">
            <input
                id="ingredient-search"
                type="text"
                className="form-input"
                placeholder="Search ingredients..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
            />
            {showDropdown && searchTerm && (
                <div className="ingredient-dropdown">
                  {isLoadingIngredients ? (
                      <div className="dropdown-item loading">Loading ingredients...</div>
                  ) : filteredIngredients.length > 0 ? (
                      filteredIngredients.slice(0, 10).map((ingredient) => (
                          <button key={ingredient.id} type="button" className="dropdown-item" onClick={() => handleAddIngredient(ingredient)}>
                            <span className="ingredient-name">{ingredient.name}</span>
                            <span className="ingredient-meta">({ingredient.category}) - {ingredient.unit}</span>
                          </button>
                      ))
                  ) : (
                      <div className="dropdown-item no-results">No ingredients found</div>
                  )}
                </div>
            )}
          </div>
        </div>

        {selectedIngredients.length > 0 && (
            <div className="selected-ingredients">
              <label>Selected Ingredients (Qty per keg)</label>
              <div className="selected-ingredients-list">
                {selectedIngredients.map((ingredient) => {
                  const errorMessage = quantityErrors[ingredient.ingredient_id];
                  const { step, min, placeholder, decimals } = getInputConfig(ingredient.unit, ingredient.category);

                  return (
                      <div key={ingredient.ingredient_id} className="selected-ingredient-item">
                        <div className="ingredient-info">
                          <span className="ingredient-name">{ingredient.ingredient_name}</span>
                          <span className="ingredient-meta">({ingredient.category}) - {ingredient.unit}</span>
                        </div>

                        <div className="ingredient-bottom-row">
                          <div className="ingredient-quantity-section">
                            <div className="ingredient-quantity">
                              {/* Custom stepper — replaces native browser spinner */}
                              <div className="quantity-stepper">
                                <button
                                    type="button"
                                    className="stepper-btn"
                                    onClick={() => handleStepDown(ingredient.ingredient_id, ingredient.quantity_per_keg, step, min, decimals)}
                                    aria-label="Decrease quantity"
                                >−</button>
                                <input
                                    id={`quantity-${ingredient.ingredient_id}`}
                                    type="number"
                                    min={min}
                                    step="any"
                                    className={`form-input quantity-input ${errorMessage ? 'error' : ''}`}
                                    placeholder={placeholder}
                                    value={ingredient.quantity_per_keg}
                                    onChange={(e) => handleTypedChange(ingredient.ingredient_id, e.target.value, decimals)}
                                    aria-describedby={errorMessage ? `quantity-error-${ingredient.ingredient_id}` : undefined}
                                />
                                <button
                                    type="button"
                                    className="stepper-btn"
                                    onClick={() => handleStepUp(ingredient.ingredient_id, ingredient.quantity_per_keg, step, min, decimals)}
                                    aria-label="Increase quantity"
                                >+</button>
                              </div>
                            </div>
                            {errorMessage && (
                                <span id={`quantity-error-${ingredient.ingredient_id}`} className="field-error" role="alert">
                          {errorMessage}
                        </span>
                            )}
                          </div>

                          <div className="ingredient-actions">
                            <button
                                type="button"
                                className="btn-remove"
                                onClick={() => handleRemoveIngredient(ingredient.ingredient_id)}
                                aria-label={`Remove ${ingredient.ingredient_name}`}
                            >
                              &times;
                            </button>
                          </div>
                        </div>
                      </div>
                  );
                })}
              </div>
            </div>
        )}

        {selectedIngredients.length === 0 && (
            <div className="no-ingredients-message">
              <p>No ingredients added yet. Search and select ingredients above.</p>
            </div>
        )}
      </div>
  );
};

export default IngredientPicker;